<template>
  <DataView
    v-bind="$props"
    :class="[
      {
        'p-dataview-rtl': isRTL
      }
    ]"
    role="region"
    :aria-label="$t('mop.dataView.ariaLabel')"
    :aria-labelledby="ariaLabelledby"
    @update:modelValue="$emit('update:modelValue', $event)"
    @update:sortField="$emit('update:sortField', $event)"
    @update:sortOrder="$emit('update:sortOrder', $event)"
    @update:first="$emit('update:first', $event)"
    @update:rows="$emit('update:rows', $event)"
    @update:layout="$emit('update:layout', $event)"
    @update:filters="$emit('update:filters', $event)"
    @update:selection="$emit('update:selection', $event)"
    @page="$emit('page', $event)"
    @sort="$emit('sort', $event)"
    @filter="$emit('filter', $event)"
    @select="handleSelect"
    @unselect="handleUnselect"
    @selectAll="handleSelectAll"
    @unselectAll="handleUnselectAll"
    @stateChange="handleStateChange"
  >
    <template v-for="(_, slot) in $slots" :key="slot" #[slot]="scope">
      <slot :name="slot" v-bind="scope"/>
    </template>
  </DataView>
</template>

<script lang="ts">
import { defineComponent, onMounted, onBeforeUnmount, ref } from 'vue'
import DataView from 'primevue/dataview'
import { useLocalization } from '../../../../composables/useLocalization'
import { dataViewProps, dataViewEmits, type DataViewStateEvent, type DataViewSelectionEvent } from './types'

export default defineComponent({
  name: 'MOPDataView',
  components: {
    DataView
  },
  props: {
    ...dataViewProps,
    ariaLabelledby: {
      type: String,
      default: null
    }
  },
  emits: Object.keys(dataViewEmits),
  setup(props, { emit }) {
    const { isRTL, t: $t } = useLocalization()
    const stateLoaded = ref(false)

    // State management
    const loadState = () => {
      if (props.stateStorage === 'none' || !props.stateKey) return

      try {
        const storage = props.stateStorage === 'local' ? localStorage : sessionStorage
        const stateString = storage.getItem(props.stateKey)

        if (stateString) {
          const state = JSON.parse(stateString)
          if (isValidState(state)) {
            emit('stateChange', state)
            stateLoaded.value = true
          } else {
            throw new Error('Invalid state format')
          }
        }
      } catch (err) {
        console.error('Error loading DataView state:', err)
        // Clear invalid state
        if (props.stateStorage === 'local') {
          localStorage.removeItem(props.stateKey)
        } else {
          sessionStorage.removeItem(props.stateKey)
        }
      }
    }

    const isValidState = (state: any): state is DataViewStateEvent => {
      return state &&
        typeof state.page === 'number' &&
        typeof state.first === 'number' &&
        typeof state.rows === 'number' &&
        (state.sortField === null || typeof state.sortField === 'string') &&
        typeof state.sortOrder === 'number' &&
        typeof state.filters === 'object'
    }

    const saveState = (state: DataViewStateEvent) => {
      if (props.stateStorage === 'none' || !props.stateKey) return

      try {
        const storage = props.stateStorage === 'local' ? localStorage : sessionStorage
        storage.setItem(props.stateKey, JSON.stringify(state))
      } catch (err) {
        console.error('Error saving DataView state:', err)
      }
    }

    const handleStateChange = (state: DataViewStateEvent) => {
      emit('stateChange', state)
      if (props.stateStorage !== 'none' && props.stateKey) {
        saveState(state)
      }
    }

    // Selection handlers with accessibility announcements
    const handleSelect = (event: DataViewSelectionEvent) => {
      emit('select', event)
      announceSelection(event.value, true)
    }

    const handleUnselect = (event: DataViewSelectionEvent) => {
      emit('unselect', event)
      announceSelection(event.value, false)
    }

    const handleSelectAll = (event: { originalEvent: Event; value: any[] }) => {
      emit('selectAll', event)
      announceToScreenReader($t('mop.dataView.allItemsSelected'))
    }

    const handleUnselectAll = (event: { originalEvent: Event; value: any[] }) => {
      emit('unselectAll', event)
      announceToScreenReader($t('mop.dataView.allItemsUnselected'))
    }

    const announceSelection = (item: any, selected: boolean) => {
      // Use item.name instead of dataKey for the announcement
      const itemLabel = item.name || item[props.dataKey] || item.toString()
      const message = selected
        ? $t('mop.dataView.itemSelected', { item: itemLabel })
        : $t('mop.dataView.itemUnselected', { item: itemLabel })
      announceToScreenReader(message)
    }

    const announceToScreenReader = (message: string) => {
      const announcement = document.createElement('div')
      announcement.setAttribute('aria-live', 'polite')
      announcement.setAttribute('class', 'sr-only')
      announcement.textContent = message
      document.body.appendChild(announcement)
      setTimeout(() => document.body.removeChild(announcement), 1000)
    }

    // Lifecycle hooks
    onMounted(() => {
      loadState()
    })

    onBeforeUnmount(() => {
      // Save final state if needed
      if (!stateLoaded.value) return

      const finalState: DataViewStateEvent = {
        page: Math.ceil((props.first || 0) / (props.rows || 10)) + 1,
        first: props.first || 0,
        rows: props.rows || 10,
        sortField: props.sortField as string || null,
        sortOrder: props.sortOrder || 1,
        filters: props.filters || {},
        selection: props.selection
      }
      saveState(finalState)
    })

    return {
      isRTL,
      $t,
      handleStateChange,
      handleSelect,
      handleUnselect,
      handleSelectAll,
      handleUnselectAll
    }
  }
})
</script>

<style lang="scss">
.p-dataview {
  &.p-dataview-rtl {
    direction: rtl;

    .p-dataview-controls {
      flex-direction: row-reverse;
    }

    .p-dataview-sort {
      flex-direction: row-reverse;
    }

    .p-dataview-layout-options {
      margin-left: 0;
      margin-right: auto;
    }

    .p-dataview-filter {
      margin-left: 0;
      margin-right: auto;
    }

    .p-paginator {
      .pi-chevron-left {
        transform: rotate(180deg);
      }

      .pi-chevron-right {
        transform: rotate(180deg);
      }
    }
  }

  .p-dataview-loading {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.7);
  }

  .p-dataview-empty-message {
    padding: 1rem;
    text-align: center;
    color: var(--text-color-secondary);
  }

  .p-dataview-grid-item {
    padding: 1rem;

    &.p-highlight {
      background: var(--highlight-bg);
      color: var(--highlight-text-color);
    }
  }

  .p-dataview-list-item {
    padding: 1rem;
    border-bottom: 1px solid var(--surface-border);

    &:last-child {
      border-bottom: none;
    }

    &.p-highlight {
      background: var(--highlight-bg);
      color: var(--highlight-text-color);
    }
  }

  // Selection styles
  &.p-dataview-selectable {
    .p-dataview-grid-item,
    .p-dataview-list-item {
      cursor: pointer;

      &:hover {
        background: var(--surface-hover);
      }
    }
  }

  // Size variants
  &.p-dataview-sm {
    .p-dataview-header,
    .p-dataview-content,
    .p-dataview-footer {
      padding: 0.5rem;
    }
  }

  &.p-dataview-lg {
    .p-dataview-header,
    .p-dataview-content,
    .p-dataview-footer {
      padding: 1.5rem;
    }
  }
}

// Screen reader only class
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
